// API Service - Handles all API calls to the serverless backend
import axios from 'axios';

// API URL configuration (priority order):
// 1. VITE_API_URL environment variable (set during build)
// 2. DEPLOYMENT_CONFIG.apiUrl (from deployment.config.js, set during deployment)
// 3. Empty string for development (uses Vite proxy)
// Note: In development, the Vite proxy handles routing, so API_URL should be empty
//       to use the proxy. Only set VITE_API_URL env var for production builds.
const isDevelopment = import.meta.env.MODE === 'development';

// Import deployment config (generated during deployment)
// In production builds, this file always exists (generated by deployment script)
// The deployment script generates it before building, so it should be available
// Use the same import pattern as App.jsx (without .js extension)
import { DEPLOYMENT_CONFIG } from '../../deployment.config';

// Determine API URL with fallback chain
const API_URL = (() => {
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http')) {
    // Priority 1: VITE_API_URL environment variable (set during build)
    return import.meta.env.VITE_API_URL;
  } else if (DEPLOYMENT_CONFIG?.apiUrl) {
    // Priority 2: API URL from deployment config (set during deployment)
    return DEPLOYMENT_CONFIG.apiUrl;
  } else if (isDevelopment) {
    // Priority 3: Empty string for development (uses Vite proxy)
    return '';
  } else {
    // Production fallback: log warning and use empty (will fail gracefully)
    console.warn('⚠️ API URL not configured! Set VITE_API_URL or ensure deployment.config.js contains apiUrl.');
    return '';
  }
})();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 120 second timeout (for large CSV imports)
});

// Debug: Log API configuration
console.log('[apiService] API_URL:', API_URL);
console.log('[apiService] DEPLOYMENT_CONFIG:', DEPLOYMENT_CONFIG);
console.log('[apiService] VITE_API_URL:', import.meta.env.VITE_API_URL);

// Add auth token interceptor
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && accessToken !== 'null' && accessToken !== 'undefined') {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      console.warn('No access token found in localStorage for API request:', config.url);
    }
    
    // For FormData, don't set Content-Type - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      console.warn('401 Unauthorized - removing token and redirecting to login');
      localStorage.removeItem('accessToken');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
      window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    return response.data;
  }
};

// App management endpoints
export const appsAPI = {
  getApps: async () => {
    const response = await api.get('/apps');
    return response.data;
  },
  
  getApp: async (appId) => {
    const response = await api.get(`/apps/${appId}`);
    return response.data;
  },
  
  switchApp: async (appId) => {
    const response = await api.post('/apps/switch', { appId });
    return response.data;
  },
  
  createApp: async (appData) => {
    const response = await api.post('/apps', appData);
    return response.data;
  },
  
  updateApp: async (appId, appData) => {
    const response = await api.put(`/apps/${appId}`, appData);
    return response.data;
  },
  
  deleteApp: async (appId) => {
    const response = await api.delete(`/apps/${appId}`);
    return response.data;
  }
};

// User management endpoints
export const usersAPI = {
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },
  
  updateProfile: async (userData) => {
    const response = await api.put('/users/profile', userData);
    return response.data;
  },
  
  getUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  }
};

// Generic API methods
export const apiService = {
  get: async (url, config = {}) => {
    const requestConfig = {
      ...config,
      timeout: config.timeout || 30000  // 30 second default for GET (increased for stats with many jobs)
    };
    const response = await api.get(url, requestConfig);
    return response.data;
  },
  
  post: async (url, data = {}, config = {}) => {
    // Create a new axios instance with custom timeout if provided
    const requestConfig = {
      ...config,
      timeout: config.timeout || 10000
    };
    const response = await api.post(url, data, requestConfig);
    return response.data;
  },
  
  put: async (url, data = {}, config = {}) => {
    const response = await api.put(url, data, config);
    return response.data;
  },
  
  delete: async (url, config = {}) => {
    // Create a new axios instance with custom timeout if provided
    const requestConfig = {
      ...config,
      timeout: config.timeout || 10000
    };
    const response = await api.delete(url, requestConfig);
    return response.data;
  }
};

export default apiService;
